import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useVendor, useVendorList } from '../../../hooks/useVendors';

const SELECT_CLASSES =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-400';

interface Option {
    value: string;
    label: string;
}

function PickerSelect({
    id,
    label,
    value,
    options,
    emptyLabel,
    disabled,
    hint,
    onChange
}: {
    id: string;
    label: string;
    value: string;
    options: Option[];
    emptyLabel: string;
    disabled?: boolean;
    hint?: React.ReactNode;
    onChange: (value: string) => void;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-xs font-semibold text-gray-700 mb-1">
                {label}
            </label>
            <select
                id={id}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className={SELECT_CLASSES}
            >
                <option value="">{emptyLabel}</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {hint && <p className="text-xs mt-1">{hint}</p>}
        </div>
    );
}

interface VendorPickerProps {
    value: string;
    disabled?: boolean;
    onChange: (vendorId: string) => void;
}

// The product's single vendor; every version's ordering method must belong to it.
export function VendorPicker({ value, disabled, onChange }: VendorPickerProps) {
    const { vendors } = useVendorList();

    // A retired vendor already on the product stays selectable, so opening an
    // old product never silently rewrites it.
    const vendorOptions: Option[] = vendors
        .filter((entry) => entry.isActive || entry.id === value)
        .map((entry) => ({
            value: entry.id,
            label: entry.isActive ? entry.name : `${entry.name} (retired)`
        }));

    return (
        <PickerSelect
            id="product-vendor"
            label="Vendor"
            value={value}
            options={vendorOptions}
            emptyLabel="None (no purchase order raised)"
            disabled={disabled}
            onChange={onChange}
            hint={<span className="text-gray-400">Drives purchase orders for this product.</span>}
        />
    );
}

interface OrderMethodPickerProps {
    id: string;
    vendorId: string;
    value: string;
    disabled?: boolean;
    onChange: (orderMethodId: string) => void;
}

// Offers only the product's vendor's methods, since that is what the server
// validates against; a vendor change surfaces the mismatch as an error.
export function OrderMethodPicker({
    id,
    vendorId,
    value,
    disabled,
    onChange
}: OrderMethodPickerProps) {
    const { vendor, isLoading: isLoadingMethods } = useVendor(vendorId || null);

    const methods = vendor?.orderMethods ?? [];
    const methodOptions: Option[] = methods
        .filter((method) => method.isActive || method.id === value)
        .map((method) => ({
            value: method.id,
            label: `${method.name} · ${method.kind === 'email' ? 'email' : 'portal'}${
                method.isActive ? '' : ' (retired)'
            }`
        }));

    const vendorSelected = vendorId.length > 0;
    const hasNoMethods = vendorSelected && !isLoadingMethods && methods.length === 0;

    return (
        <PickerSelect
            id={id}
            label="Ordering method"
            value={value}
            options={methodOptions}
            emptyLabel={
                !vendorSelected
                    ? 'Pick a vendor first'
                    : isLoadingMethods
                      ? 'Loading methods…'
                      : 'None (no purchase order raised)'
            }
            disabled={disabled || !vendorSelected || isLoadingMethods || hasNoMethods}
            onChange={onChange}
            hint={
                hasNoMethods ? (
                    <span className="inline-flex items-start gap-1 text-rose-600">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 mt-px" />
                        This vendor has no ordering methods. Add one on the Vendors page.
                    </span>
                ) : value.length === 0 && vendorSelected ? (
                    <span className="text-amber-600">
                        Without a method no purchase order is raised for this version.
                    </span>
                ) : undefined
            }
        />
    );
}
